import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import { IInvoice, IInvoiceLine } from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { MenuModule } from "primeng/menu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { CustomerService } from "../../../../../shared/services/customers/customers.service.mjs";
import { InvoiceService } from "../../../../../shared/services/financial/invoice.service.mjs";

@Component({
  selector: "app-invoice-view",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ConfirmDialogModule,
    BlockUIModule,
    CardModule,
    BreadcrumbModule,
    ToastModule,
    ButtonModule,
    ProgressSpinnerModule,
    MenuModule,
    RouterLink,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: "./invoice-view.component.html",
  styleUrls: ["./invoice-view.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class InvoiceViewComponent implements OnInit {
  object: IInvoice | null = null; // Store the selected conduit details
  isLoading = false;

  breadCrumbItems: MenuItem[] | undefined;

  home: MenuItem | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: InvoiceService,
    private customerService: CustomerService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  items: MenuItem[] = [
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => this.delete(),
    },
    {
      label: "Re-instate",
      icon: "pi pi-check",
      command: () => this.reinstate(),
    },
    {
      label: "Mark Paid",
      icon: "pi pi-dollar",
      command: () => this.markPaid(),
    },
  ];

  async markPaid() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to mark this invoice as paid?",
      header: "Mark Invoice Paid",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Mark Invoice Paid",
        severity: "primary",
      },
      accept: async () => {
        this.isLoading = true;

        this.service
          .markPaid(this.object!.id)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice marked as paid ....",
              });

              this.object = result.data!;
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to mark invoice paid ....",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  async edit() {
    this.router.navigate(["/admin/invoices/edit", this.object?.id]);
  }

  async delete() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to delete this invoice?",
      header: "Delete Invoice",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Delete Invoice",
      },
      accept: async () => {
        this.isLoading = true;
        await this.service
          .delete(this.object!)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice deleted successfully ..... redirecting",
              });
              this.router.navigate([
                "/admin/customers/view",
                this.object?.customer_id.id,
              ]);
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Failed",
                detail: "Failed to delete the invoice",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  async reinstate() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to re-instate this invoice?",
      header: "Re-instate Invoice",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Re-instate Invoice",
      },
      accept: async () => {
        this.isLoading = true;
        await this.service
          .reinstate(this.object!.id)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice re-instated successfully ..... redirecting",
              });
              this.router.navigate([
                "/admin/customers/view",
                this.object?.customer_id.id,
              ]);
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Failed",
                detail: "Failed to re-instate the invoice",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  async sendDocument() {
    await this.customerService.sendInvoice(this.object!.id);
  }
  
  ngOnInit(): void {
    this.breadCrumbItems = [
      {
        label: "Invoices",
        command: () => this.router.navigate(["/admin/invoices"]),
      },
      {
        label: "Customers",
        command: () => this.router.navigate(["/admin/customers"]),
      },
    ];

    this.home = { icon: "pi pi-home", routerLink: "/" };

    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.load(id);
    }
  }

  load(id: string): void {
    this.isLoading = true;
    this.service
      .get(id)
      .then((response) => {
        this.object = response?.data ?? null; // Handle null or undefined data
        this.breadCrumbItems?.push({
          label: this.object?.customer?.name ?? "Customer",
          command: () =>
            this.router.navigate([
              "/admin/customers/view",
              this.object?.customer?.id,
            ]),
        });
        this.breadCrumbItems?.push({ label: "Invoice" });

        this.isLoading = false;
      })
      .catch((error) => {
        console.error("Error fetching subscription details:", error);
        this.isLoading = false;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  get showDiscountColumn(): boolean {
    if (this.object && this.object.lines) {
      return this.object.lines.data.some(
        (line: IInvoiceLine) =>
          line.discount_total !== undefined && line.discount_total > 0
      );
    }
    return false;
  }

  calculateDerivedTotals(): void {
    if (this.object) {
      const taxableAmount =
        this.object.excluding_tax - this.object.discount_value;
      this.object.tax = taxableAmount * 0.1;
      this.object.including_tax = taxableAmount + this.object.tax;
    }
  }

  formatCurrency(amount: number | undefined): string {
    if (amount === undefined) return "N/A";
    return amount.toFixed(2);
  }
}
